
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '@/components/Navbar';
import PageTransition from '@/components/PageTransition';
import { AnimatedButton } from '@/components/AnimatedButton';
import { Separator } from '@/components/ui/separator';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  toggleOpen: () => void;
  index: number;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, toggleOpen, index }) => {
  return (
    <div className="opacity-0 animate-fade-up" style={{ animationDelay: `${index * 50}ms` }}>
      <div 
        className="border border-border rounded-lg p-5 mb-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={toggleOpen}
      >
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">{question}</h3>
          <button className="text-muted-foreground">
            {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>
        {isOpen && (
          <div className="mt-4 text-muted-foreground animate-fade-down">
            <p>{answer}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openFAQs, setOpenFAQs] = React.useState<{ [key: number]: boolean }>({});

  const toggleFAQ = (index: number) => {
    setOpenFAQs(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const faqItems = [
    {
      category: "General Information",
      items: [
        {
          question: "What is the Emergency Preparedness Assistant?",
          answer: "A conversational AI-powered tool that creates personalized emergency preparedness plans based on your unique circumstances, including family dynamics, home environment, and medical needs."
        },
        {
          question: "How does the Emergency Preparedness Assistant work?",
          answer: "The assistant conducts a friendly interview through chat, asking questions about your location, family, home, and specific needs. It then analyzes this information and uses extensive research capabilities to generate a customized emergency plan for you."
        },
        {
          question: "What types of emergencies does the planner cover?",
          answer: "The planner covers various emergency scenarios including earthquakes, wildfires, floods, and other natural disasters relevant to your geographical location."
        }
      ]
    },
    {
      category: "Features and Functionality",
      items: [
        {
          question: "Can I upload images of my home?",
          answer: "Yes, you can upload pictures of your property. The system will analyze these images to identify potential hazards like steep slopes or overhanging trees."
        },
        {
          question: "Will the plan include product recommendations?",
          answer: "Yes, the system can generate a shopping list of recommended emergency supplies based on your specific needs and budget."
        },
        {
          question: "Does the planner provide information about local emergency services?",
          answer: "Yes, the planner includes recommendations for local specialist services such as engineering firms, fire protection services, and home safety auditors in your area."
        },
        {
          question: "Can I get separate plans for different types of emergencies?",
          answer: "Yes, premium subscribers can choose between a comprehensive plan or separate plans for individual emergency scenarios."
        }
      ]
    },
    {
      category: "Privacy and Security",
      items: [
        {
          question: "How is my personal information protected?",
          answer: "All data is encrypted both in transit and at rest. The system complies with data protection regulations including GDPR and applicable privacy laws."
        },
        {
          question: "Who has access to the information I provide?",
          answer: "Your information is only used to generate your emergency plan. We do not share your personal data with third parties except as required to provide the service (such as local specialist recommendations)."
        },
        {
          question: "Are my uploaded images stored securely?",
          answer: "Yes, all uploaded images are stored with encryption and are only used for hazard analysis purposes."
        }
      ]
    },
    {
      category: "Subscription and Pricing",
      items: [
        {
          question: "What's the difference between free and premium subscriptions?",
          answer: "Premium subscribers receive ongoing chat support with their plan, regular updates as new research becomes available, and the ability to switch between comprehensive and single-scenario plans."
        },
        {
          question: "How often can I update my emergency plan?",
          answer: "Premium subscribers can update their plans whenever their circumstances change or new information becomes available."
        },
        {
          question: "Can I cancel my subscription at any time?",
          answer: "Yes, you can manage your subscription through your account settings and cancel at any time."
        },
        {
          question: "What happens if I move to a new location?",
          answer: "Plans are priced on a per-instance basis, meaning each new plan requires a separate purchase. Plans can be purchased in bundles, however, and unused plan credits don't expire, so if you have unused plan credits, those can be applied to creating a plan for your new location."
        }
      ]
    },
    {
      category: "Technical Requirements",
      items: [
        {
          question: "What devices can I use to access the Emergency Preparedness Assistant?",
          answer: "The assistant is accessible via web browsers on desktop computers, tablets, and mobile devices with an internet connection."
        },
        {
          question: "Do I need to install any software to use the planner?",
          answer: "No, the Emergency Preparedness Assistant is a web-based application that works in standard browsers without additional software."
        },
        {
          question: "Can I download and print my emergency plan?",
          answer: "Yes, your plan is available as a downloadable and printable PDF document."
        }
      ]
    },
    {
      category: "Amazon Integration",
      items: [
        {
          question: "How does the Amazon integration work?",
          answer: "If you choose to connect your Amazon account, the system can generate a shopping list of recommended emergency supplies and add them directly to your Amazon cart."
        },
        {
          question: "Is connecting my Amazon account required?",
          answer: "No, this is an optional feature. You can still receive product recommendations without connecting your Amazon account."
        }
      ]
    },
    {
      category: "Support and Help",
      items: [
        {
          question: "What if I need help using the Emergency Preparedness Assistant?",
          answer: "Customer support is available through the help section in your dashboard, where you can find guides and contact support agents."
        },
        {
          question: "How often is the emergency information updated?",
          answer: "The system regularly incorporates the latest research and best practices in emergency preparedness. Premium subscribers receive notifications when significant updates are available."
        },
        {
          question: "Can I share my emergency plan with family members?",
          answer: "Yes, you can share your plan with family members by downloading the PDF and distributing it or by granting them access through your account settings."
        }
      ]
    }
  ];

  // Calculate total number of FAQs for indexing
  let globalIndex = 0;

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-[200px] md:pt-[200px] pb-16">
          <div className="container px-4 md:px-6">
            <div className="text-center max-w-[800px] mx-auto mb-12 opacity-0 animate-fade-up">
              <h1 className="hero-text text-balance mb-6">
                Frequently Asked <span className="text-primary">Questions</span>
              </h1>
              <p className="body-lg text-muted-foreground mb-8 mx-auto max-w-[650px]">
                Find answers to common questions about our emergency planning services.
              </p>
            </div>
          </div>
        </section>
        
        {/* FAQ Sections */}
        <section className="py-12">
          <div className="container px-4 md:px-6">
            {faqItems.map((category, categoryIndex) => {
              return (
                <div key={categoryIndex} className="mb-12">
                  <h2 className="heading-lg mb-6 opacity-0 animate-fade-up" style={{ animationDelay: `${categoryIndex * 100}ms` }}>
                    {category.category}
                  </h2>
                  <Separator className="mb-6" />
                  
                  {category.items.map((item, itemIndex) => {
                    const currentGlobalIndex = globalIndex++;
                    return (
                      <FAQItem 
                        key={`${categoryIndex}-${itemIndex}`}
                        question={item.question}
                        answer={item.answer}
                        isOpen={!!openFAQs[currentGlobalIndex]}
                        toggleOpen={() => toggleFAQ(currentGlobalIndex)}
                        index={itemIndex}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-20">
          <div className="container px-4 md:px-6">
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 md:p-10 relative overflow-hidden">
              <div className="relative z-10 max-w-[600px] mx-auto text-center opacity-0 animate-fade-up">
                <h2 className="heading-lg mb-4">Ready to start your emergency planning?</h2>
                <p className="body-base text-muted-foreground mb-8">
                  Join thousands of families and businesses who trust EmergencyPlan 
                  for their safety and preparedness needs.
                </p>
                <Link to="/signup">
                  <AnimatedButton size="lg">
                    Get Started Today
                  </AnimatedButton>
                </Link>
              </div>
            </div>
          </div>
        </section>
        
        {/* Footer */}
        <footer className="py-12 bg-muted/30 border-t border-border">
          <div className="container px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="col-span-1 md:col-span-2">
                <h3 className="text-lg font-bold mb-4">EmergencyPlan</h3>
                <p className="text-muted-foreground mb-4 max-w-[360px]">
                  AI-powered emergency planning for families and businesses. 
                  Stay prepared for any situation.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-4">Pages</h4>
                <ul className="space-y-2">
                  <li><Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Home</Link></li>
                  <li><Link to="/features" className="text-sm text-muted-foreground hover:text-foreground">Features</Link></li>
                  <li><Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground">Pricing</Link></li>
                  <li><Link to="/about" className="text-sm text-muted-foreground hover:text-foreground">About</Link></li>
                  <li><Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground">FAQ</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-4">Legal</h4>
                <ul className="space-y-2">
                  <li><Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground">Terms of Service</Link></li>
                </ul>
              </div>
            </div>
            <div className="mt-12 pt-8 border-t border-border">
              <p className="text-sm text-muted-foreground text-center">
                © {new Date().getFullYear()} EmergencyPlan. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

export default FAQ;
